'use client';

import { useQuery } from '@tanstack/react-query';
import styled from 'styled-components';

import { AlarmAdd, AlarmCheck, CheckOutlined, Shop } from '@zoomit/icons';
import { Flex, Spacing } from '@zoomit/primitives';
import type { BaseImageType} from '@zoomit/web-kit';
import { PriceNotifyModal, usePriceNotify } from '@zoomit/web-kit';
import { Anchor, Button, Typography, cssVar, theme } from '@zoomit/zoomit-ds';

import { getPriceChart } from '../../apis/product';
import { STORES_ID } from '../../constants/single-product';
import RangePrice from '../shared/RangePrice';

type Props = {
  priceFrom: number;
  productId: string;
  priceTo: number;
  inStock: boolean;
  hasPriceChart: boolean;
  image: BaseImageType;
  title: string;
};

const PriceChartMobile = (props: Props) => {
  const { priceFrom, productId, priceTo, inStock, hasPriceChart, title, image } = props;

  const { data: priceChartData, isFetching } = useQuery(
    ['price-chart', `productId:${productId}`, `range:All`],
    async () => {
      const res = await getPriceChart(productId, 'All');
      return res.data;
    },
    {
      enabled: hasPriceChart,
    },
  );

  const { priceNotifyPopOverState, notifyQuery, deleteMutation, postMutation } = usePriceNotify({
    productId,
  });

  return (
    <Root inStock={inStock} isLoading={isFetching}>
      {inStock ? (
        <div>
          <PriceRangeWrapper>
            <RangePrice priceFrom={priceFrom} priceTo={priceTo} fontVariant="large" />
          </PriceRangeWrapper>
          <Flex alignItems="flex-end" flexWrap="nowrap" gap={0.8} spacing={{ mt: 'x4' }}>
            <Anchor
              href={`#${STORES_ID}`}
              variant="contained"
              size="full width"
              fontWeight="bold"
              rightIcon={<Shop className="!fill-grey3" size="sm" />}
            >
              مشاهده فروشندگان
            </Anchor>
            {notifyQuery.data ? (
              <Button
                variant="outline"
                size="fit"
                style={{ padding: '0.9rem' }}
                onClick={() => deleteMutation.mutate()}
              >
                <AlarmCheck size="sm" fill={cssVar(theme.success1)} />
              </Button>
            ) : (
              <Button
                variant="outline"
                size="fit"
                style={{ padding: '0.9rem' }}
                onClick={priceNotifyPopOverState.open}
              >
                <AlarmAdd size="sm" className="fill-grey3" />
              </Button>
            )}
          </Flex>
        </div>
      ) : (
        <Flex alignItems="center" flexDirection="column" gap={1.6}>
          <Typography
            tag="div"
            fontSize={1.4}
            fontWeight="bold"
            textColor={cssVar(theme.grey1)}
            textAlign="center"
            lineHeight={2}
          >
            این محصول فعلا فروشنده‌ای ندارد
          </Typography>
          {notifyQuery.data ? (
            <ActionButton
              variant="outline"
              fontWeight="normal"
              size="full width"
              rightIcon={<CheckOutlined className="!fill-success1" size="sm" />}
              onClick={() => deleteMutation.mutate()}
            >
              <Typography fontSize={1.2} textColor={cssVar(theme.success1)} fontWeight="bold">
                موجود شد خبرت می‌کنیم
              </Typography>
            </ActionButton>
          ) : (
            <ActionButton
              variant="outline"
              fontWeight="normal"
              size="full width"
              rightIcon={<AlarmAdd className="!fill-text" size="sm" />}
              onClick={priceNotifyPopOverState.open}
            >
              <Typography fontSize={1.2} textColor={cssVar(theme.text)} fontWeight="bold">
                موجود شد خبرم کن
              </Typography>
            </ActionButton>
          )}
        </Flex>
      )}
      <PriceNotifyModal
        productId={productId}
        title={title}
        image={image}
        onClose={priceNotifyPopOverState.close}
        isPriceNotifyShow={priceNotifyPopOverState.isOpen}
        onSetNotify={() => postMutation.mutate()}
      />
    </Root>
  );
};

export default PriceChartMobile;

const Root = styled.div<{ inStock: boolean; isLoading: boolean }>`
  border-radius: 0;
  width: 100%;
  display: flex;
  flex-direction: column-reverse;
  justify-content: ${({ inStock }) => (inStock ? 'space-between' : 'center')};
  gap: ${({ isLoading }) => (isLoading ? '3.2rem' : '0')};
  padding: ${({ inStock }) => (inStock ? '0' : `0 0 ${Spacing.x2}rem`)};
`;

const PriceRangeWrapper = styled(Flex)`
  height: 3.2rem;
  align-items: center;
  justify-content: center;
`;

const ActionButton = styled(Button)`
  padding: 0;
  font-size: 13px;
`;
